import * as ModelEvent from "@node-elion/syncron";
import { uniq } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { createObjectLanguageNames } from "../../assets/languages/langs";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { SortingOrder } from "../../types/SortingOrder";
import SubscriptionBase from "../../types/Subscription";
import { Base, Language, TaxiService } from "..";

import type {
	OrderCancellationConfiguration,
	ExecutorPenaltyManualRule,
} from "../../types/executorPenalty";

const logger = createLogger({ name: "ExecutorPenalty" });

function taxiServiceIds(data: { taxiServices?: { id: number }[] }): number[] {
	try {
		const taxiServices = data?.taxiServices ?? [];
		if (!taxiServices.length) return [];
		return uniq<number>(
			taxiServices.map((item) => item?.id).filter(Boolean),
		);
	} catch {
		return [];
	}
}

class ExecutorPenalty extends Base {
	static fromResponse(
		data: Record<string, unknown>,
		lang: Language = "uk",
	): ExecutorPenalty.Model {
		const ids = taxiServiceIds(data as { taxiServices?: { id: number }[] });
		const executors = (data?.executors as { id?: number }[]) ?? [];
		const executorIds = executors
			.map((e) => e?.id)
			.filter((id): id is number => typeof id === "number");
		return {
			id: (data?.id as number) ?? 0,
			active: (data?.active as boolean) ?? true,
			isDefault: (data?.isDefault as boolean) ?? false,
			root: (data?.root as boolean) ?? false,
			name:
				(data?.name as Record<string, string>)?.[lang] ??
				(data?.name as Record<string, string>)?.ru ??
				"",
			taxiServiceIds: (data?.taxiServiceIds as number[]) ?? ids,
			taxiServices: (data?.taxiServices as TaxiService.Model[]) ?? [],
			orderCancellationConfiguration: mergeWithDefaults(
				(data?.orderCancellationConfiguration as OrderCancellationConfiguration) ??
					defaultOrderCancellationConfiguration(),
			),
			manualRules:
				(data?.manualRules as ExecutorPenaltyManualRule[]) ?? [],
			createdAt: (data?.createdAt as string) ?? "",
			updatedAt: (data?.updatedAt as string) ?? "",
			deletedAt: (data?.deletedAt as string) ?? null,
			executorIds,
		};
	}

	static toRequest(
		model: ExecutorPenalty.Model.New | ExecutorPenalty.Model.Modified,
	): Record<string, unknown> {
		const result: Record<string, unknown> = {
			name: createObjectLanguageNames(model.name),
			active: model.active,
			isDefault: model.isDefault,
			taxiServiceIds: model.taxiServiceIds,
			orderCancellationConfiguration:
				model.orderCancellationConfiguration,
			manualRules: model.manualRules ?? [],
		};
		if (
			"executorIds" in model &&
			Array.isArray((model as ExecutorPenalty.Model.Modified).executorIds)
		) {
			result.executorIds = (
				model as ExecutorPenalty.Model.Modified
			).executorIds;
		}
		return result;
	}

	public static async getById(id: number, lang?: Language) {
		const data = await this.request((prpc) =>
			prpc.theirsModel.executorPenalty.getById(id),
		);
		if (!data) return null;
		return ExecutorPenalty.fromResponse(
			data as Record<string, unknown>,
			lang,
		);
	}

	public static async store(
		object: ExecutorPenalty.Model.New,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorPenalty.create(
						ExecutorPenalty.toRequest(object) as Record<
							string,
							unknown
						>,
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("ExecutorPenalty store", { object, res, force });
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async update(
		object: ExecutorPenalty.Model.Modified,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorPenalty.update(
						object.id,
						ExecutorPenalty.toRequest(object) as Record<
							string,
							unknown
						>,
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("ExecutorPenalty update", {
				object,
				res,
				force,
			});
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async apply(params: ExecutorPenalty.ApplyParams) {
		await this.request(
			(prpc) => prpc.theirsModel.executorPenalty.apply(params),
			{ silent: false, error: true },
		);
	}

	public static async destroy(id: number[] | number) {
		if (Array.isArray(id))
			await Promise.all(id.map((idItem) => this.destroyOne(idItem)));
		else await this.destroyOne(id);
	}

	public static async subscribe(
		options: ExecutorPenalty.SubscribeOptions,
		onUpdate: SubscriptionBase.OnUpdate<ExecutorPenalty.Model>,
	): Promise<SubscriptionBase<ExecutorPenalty.SubscribeOptions> | null> {
		const currentOptionsRef: { current: ExecutorPenalty.SubscribeOptions } =
			{ current: options };

		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				const opts = currentOptionsRef.current;
				logger.info("[ExecutorPenalty] subscribe", {
					options: opts,
					state,
				});
				onUpdate({
					...state,
					models: (state.models as Record<string, unknown>[]).map(
						(item) =>
							ExecutorPenalty.fromResponse(
								item as Record<string, unknown>,
								opts.lang,
							),
					),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.executorPenalty.subscribe({
					params: ExecutorPenalty.optionsToRequest(options),
					ping: () => true,
					onEvent: (event: unknown) => {
						modelEventConstructor.onEvent(
							event as Parameters<
								ModelEvent.ModelEventConstructor["onEvent"]
							>[0],
						);
					},
					onError: (error: unknown) => {
						logger.error(error);
					},
				}),
			{ name: "ExecutorPenalty.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (opts: ExecutorPenalty.SubscribeOptions) => {
				currentOptionsRef.current = opts;
				return subscription.update(
					ExecutorPenalty.optionsToRequest(opts),
				);
			},
		} as SubscriptionBase<ExecutorPenalty.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		this.request((prpc) => prpc.theirsModel.executorPenalty.delete(id));
	}

	private static optionsToRequest(options: ExecutorPenalty.SubscribeOptions) {
		return {
			query: options.query,
			offset: options.offset,
			limit: options.limit,
			lang: options.lang,
			taxiServiceIds: options.taxiServiceIds,
			order: options.order,
		};
	}
}

function defaultOrderCancellationConfiguration(): OrderCancellationConfiguration {
	return {
		orderRefusals: { active: false, rules: {} },
		orderCancellations: {
			active: false,
			dispatcherRemoval: { active: false, rules: {} },
			afterAcceptance: { active: false, rules: {} },
			beforeArrival: { active: false, rules: {} },
			afterDelivery: { active: false, rules: {} },
		},
	};
}

function mergeWithDefaults(
	input: OrderCancellationConfiguration,
): OrderCancellationConfiguration {
	const defaultConf = defaultOrderCancellationConfiguration();
	return {
		...defaultConf,
		...input,
		orderRefusals: { ...defaultConf.orderRefusals, ...input.orderRefusals },
		orderCancellations: {
			...defaultConf.orderCancellations,
			...input.orderCancellations,
			dispatcherRemoval: {
				...defaultConf.orderCancellations.dispatcherRemoval,
				...input.orderCancellations?.dispatcherRemoval,
			},
			afterAcceptance: {
				...defaultConf.orderCancellations.afterAcceptance,
				...input.orderCancellations?.afterAcceptance,
			},
			beforeArrival: {
				...defaultConf.orderCancellations.beforeArrival,
				...input.orderCancellations?.beforeArrival,
			},
			afterDelivery: {
				...defaultConf.orderCancellations.afterDelivery,
				...input.orderCancellations?.afterDelivery,
			},
		},
	};
}

declare namespace ExecutorPenalty {
	export interface Model {
		id: number;
		active: boolean;
		isDefault: boolean;
		root: boolean;
		name: string;
		taxiServiceIds: number[];
		taxiServices?: TaxiService.Model[];
		orderCancellationConfiguration: OrderCancellationConfiguration;
		manualRules: ExecutorPenaltyManualRule[];
		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
		executorIds?: number[];
	}

	export interface ApplyParams {
		executorIds: number[];
		penaltyType: string;
		orderId?: number;
		amount?: number;
		comment?: string;
		idempotencyKey?: string;
	}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<ExecutorPenalty.Model>,
			"order"
		> {
		taxiServiceIds?: number[];
		lang?: Language;
		order?: Partial<Record<keyof Model, SortingOrder>>;
	}

	namespace Model {
		interface New {
			name: string;
			active: boolean;
			isDefault: boolean;
			taxiServiceIds: number[];
			orderCancellationConfiguration: OrderCancellationConfiguration;
			manualRules?: ExecutorPenaltyManualRule[];
		}
		type Modified = Partial<New> & { id: number; executorIds?: number[] };
	}
}

export default ExecutorPenalty;
